import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { NIL } from 'uuid';
import { RecurringRevenuesDto, RecurringPendingEnrollmentsDto } from '../models/recurringRevenue';

const apiUrl = () => process.env.REACT_APP_BASIX_API || '';

// item 63c — company-wide list for the Recurring Revenue Management page.
export const getRecurringRevenues = async (): Promise<RecurringRevenuesDto> => {
  const { data } = await axios.get(`${apiUrl()}RecurringRevenue`);
  return data;
};

export const useRecurringRevenues = () => useQuery('recurringRevenue', getRecurringRevenues);

// item 170 — the archived schedules (the Archived view; fetched only when shown).
export const getArchivedRecurringRevenues = async (): Promise<RecurringRevenuesDto> => {
  const { data } = await axios.get(`${apiUrl()}RecurringRevenue/Archived`);
  return data;
};

export const useArchivedRecurringRevenues = (enabled: boolean) => useQuery(
  ['recurringRevenue', 'archived'],
  getArchivedRecurringRevenues,
  { enabled },
);

// item 63a — the schedules under one project (per-project section).
export const useRecurringRevenuesByProject = (projectId?: string) => useQuery(
  ['recurringRevenue', 'project', projectId],
  async (): Promise<RecurringRevenuesDto> => {
    const { data } = await axios.get(`${apiUrl()}RecurringRevenue/ProjectId/${projectId}`);
    return data;
  },
  { enabled: !!projectId && projectId !== NIL },
);

// item 706 — the "not enrolled" list: sold subscription proposals whose project has no live
// recurring record yet. Feeds the Recurring section's needs-enrollment rows + the header count.
export const usePendingRecurringEnrollments = () => useQuery(
  ['recurringRevenue', 'pending-enrollment'],
  async (): Promise<RecurringPendingEnrollmentsDto> => {
    const { data } = await axios.get(`${apiUrl()}RecurringRevenue/PendingEnrollment`);
    return data;
  },
);

// item 731g / 730 (Chad) — adopt existing live Stripe subscriptions.
export interface StripeSubscriptionPreview {
  stripeSubscriptionId: string;
  stripeCustomerId?: string | null;
  customerEmail?: string | null;
  customerName?: string | null;
  amount: number;
  frequency: number;
  customIntervalDays?: number | null;
  status?: string | null;
  autoPay: boolean;
  alreadyImported: boolean;
  matchedCustomerId?: string | null;
  matchedCustomerName?: string | null;
  // item 1125 — the human-readable difference between same-customer/same-amount subs:
  // the Stripe product lines, the sub's description, and when it started
  description?: string | null;
  created?: string | null;
  lines?: string[];
}
export interface StripeSubscriptionsPreviewDto {
  available: boolean;
  items: StripeSubscriptionPreview[];
}
export interface ImportSubscriptionInput {
  stripeSubscriptionId: string;
  customerId: string;
  projectId: string;
  name: string;
}

// item 1128 — live Stripe line detail for ONE sub (the hover on sub rows); server caches 5 min
export interface StripeSubDetail {
  available: boolean;
  status?: string | null;
  autoPay?: boolean;
  description?: string | null;
  // item 1130 — the dashboard's "Invoice memo" / footer / custom fields (client info lives
  // in the memo on legacy subs); Stripe stamps them per invoice, read from latest_invoice
  memo?: string | null;
  footer?: string | null;
  customFields?: string[];
  created?: string | null;
  lines?: string[];
  error?: string | null;
}
export const useStripeSubDetail = (recurringId: string, enabled: boolean) => useQuery(
  ['recurringRevenue', 'stripe-detail', recurringId],
  async (): Promise<StripeSubDetail> => {
    const { data } = await axios.get(`${apiUrl()}payment/subscription-detail/${recurringId}`);
    return data;
  },
  { enabled, staleTime: 5 * 60_000 },
);

// read-only dry-run of every live Stripe subscription (enabled only when the modal opens)
export const useStripeSubscriptionsPreview = (enabled: boolean) => useQuery(
  ['recurringRevenue', 'stripe-preview'],
  async (): Promise<StripeSubscriptionsPreviewDto> => {
    const { data } = await axios.get(`${apiUrl()}payment/stripe-subscriptions`);
    return data;
  },
  { enabled, staleTime: 0 },
);

// commit the adoption (create linked RecurringRevenue records for the chosen subs)
export const useImportStripeSubscriptions = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (items: ImportSubscriptionInput[]) => axios
      .post(`${apiUrl()}payment/import-subscriptions`, items)
      .then((r) => r.data as { imported: number; skipped: string[] }),
    {
      onSettled: () => Promise.all([
        queryClient.invalidateQueries('recurringRevenue'),
        queryClient.invalidateQueries('projects'),
      ]),
    },
  );
};

// item 731 (Chad) — decline (or undo) a mistaken pending enrollment: drops the proposal off the
// "Subscriptions to enroll" list without touching the proposal. Refreshes the pending list + the
// proposal-derived project data (the PENDING count rides ProposalIndexItemDto.PendingRecurring).
export const useDeclineRecurringEnrollment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars: { proposalId: string; declined: boolean }) => axios.post(
      `${apiUrl()}RecurringRevenue/DeclineEnrollment/${vars.proposalId}`,
      vars.declined,
      { headers: { 'content-type': 'application/json' } },
    ),
    {
      onSettled: () => Promise.all([
        queryClient.invalidateQueries('recurringRevenue'),
        queryClient.invalidateQueries('proposals'),
        queryClient.invalidateQueries('project'),
        queryClient.invalidateQueries('projects'),
      ]),
    },
  );
};

export default useRecurringRevenues;
